/**
 * @fileoverview MiniAudioPlayer Component - Draggable mini audio player
 * 
 * Provides a compact, draggable audio player interface with:
 * - Draggable positioning anywhere on screen
 * - Compact audio controls and information display
 * - Platform-specific audio icons and colors
 * - Progress visualization for direct audio files
 * - Expand to full player functionality
 * - Click-to-expand with drag detection
 * - Viewport boundary constraints
 * - Audio metadata display
 * - Platform integration (Spotify, SoundCloud, etc.)
 * 
 * @component
 * @example
 * <MiniAudioPlayer />
 * 
 * @requires React
 * @requires ../../contexts/AudioContext
 * @requires ./MiniAudioPlayer.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAudio } from '../../contexts/AudioContext';
import './MiniAudioPlayer.css';

/**
 * MiniAudioPlayer Component
 * 
 * Renders a compact, draggable audio player that can be positioned anywhere on screen.
 * Provides essential audio controls and information in a minimal interface.
 * 
 * Features:
 * - Draggable positioning with viewport constraints
 * - Compact audio controls (play/pause, expand, close)
 * - Platform-specific icons and colors
 * - Progress visualization for direct audio
 * - Click-to-expand with drag detection
 * - Audio metadata display
 * - Platform integration support
 * - Responsive design
 * 
 * @component
 * @returns {JSX.Element|null} Draggable mini audio player or null if hidden
 */
const MiniAudioPlayer = () => {
    const {
        currentAudio,
        isPlaying,
        currentTime,
        duration,
        isEmbedded,
        showMiniPlayer,
        togglePlayback,
        closeAudio,
        restoreToFull,
        formatTime
    } = useAudio();

    // Dragging state
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 20, y: 20 }); // Default top-right position
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
    const [hasDragged, setHasDragged] = useState(false);
    const playerRef = useRef(null);

    // Drag handlers
    const handleMouseDown = (e) => {
        if (e.target.closest('.mini-controls')) return; // Don't drag when clicking controls
        
        setIsDragging(true);
        setHasDragged(false); // Reset drag flag
        const rect = playerRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setDragStartPos({ x: e.clientX, y: e.clientY }); // Remember start position
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        // Check if we've moved enough to consider this a drag (5px threshold)
        const dragDistance = Math.sqrt(
            Math.pow(e.clientX - dragStartPos.x, 2) + 
            Math.pow(e.clientY - dragStartPos.y, 2)
        );
        
        if (dragDistance > 5) {
            setHasDragged(true);
        }
        
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Keep within viewport bounds
        const maxX = window.innerWidth - (playerRef.current?.offsetWidth || 320);
        const maxY = window.innerHeight - (playerRef.current?.offsetHeight || 80);
        
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));
        
        setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Add global mouse event listeners when dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none'; // Prevent text selection while dragging
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.userSelect = '';
            };
        }
    }, [isDragging, dragOffset]);

    if (!showMiniPlayer || !currentAudio) {
        return null;
    }

    const getAudioPlatformIcon = (url) => {
        if (url.includes('spotify.com')) return 'fab fa-spotify';
        if (url.includes('soundcloud.com')) return 'fab fa-soundcloud';
        if (url.includes('anchor.fm')) return 'fas fa-anchor';
        return 'fas fa-headphones';
    };

    const getAudioPlatformColor = (url) => {
        if (url.includes('spotify.com')) return '#1DB954';
        if (url.includes('soundcloud.com')) return '#FF5500';
        if (url.includes('anchor.fm')) return '#5000FF';
        return 'var(--ifia-green)';
    };

    return (
        <div 
            ref={playerRef}
            className={`mini-audio-player ${isDragging ? 'dragging' : ''}`}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                right: 'auto', // Override CSS right positioning
                cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleMouseDown}
        >
            <div 
                className="mini-player-content" 
                onClick={(e) => {
                    // Only restore to full if we haven't actually dragged
                    if (!hasDragged) {
                        restoreToFull();
                    }
                }}
            >
                {/* Audio thumbnail/icon */}
                <div className="mini-thumbnail">
                    <div 
                        className="mini-thumbnail-bg"
                        style={{ backgroundColor: getAudioPlatformColor(currentAudio.webURLLink) }}
                    >
                        <i className={getAudioPlatformIcon(currentAudio.webURLLink)}></i>
                    </div>
                </div>

                {/* Audio info */}
                <div className="mini-info">
                    <div className="mini-title" title={currentAudio.topic}>
                        {currentAudio.topic}
                    </div>
                    <div className="mini-meta">
                        {isEmbedded ? (
                            <span className="mini-platform">
                                {currentAudio.webURLLink.includes('spotify.com') ? 'Spotify' :
                                 currentAudio.webURLLink.includes('soundcloud.com') ? 'SoundCloud' :
                                 currentAudio.webURLLink.includes('anchor.fm') ? 'Anchor' : 'Audio'}
                            </span>
                        ) : (
                            <span className="mini-time">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </span>
                        )}
                    </div>
                </div>

                {/* Progress bar for direct audio */}
                {!isEmbedded && duration > 0 && (
                    <div className="mini-progress-bar">
                        <div 
                            className="mini-progress-fill"
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                        ></div>
                    </div>
                )}
            </div>

            {/* Controls */}
            <div className="mini-controls">
                {!isEmbedded && (
                    <button 
                        className="mini-control-btn play-pause"
                        onClick={(e) => {
                            e.stopPropagation();
                            togglePlayback();
                        }}
                        title={isPlaying ? 'Pause' : 'Play'}
                    >
                        <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
                    </button>
                )}
                
                <button 
                    className="mini-control-btn expand"
                    onClick={(e) => {
                        e.stopPropagation();
                        restoreToFull();
                    }}
                    title="Expand player"
                >
                    <i className="fas fa-expand"></i>
                </button>
                
                <button 
                    className="mini-control-btn close"
                    onClick={(e) => {
                        e.stopPropagation();
                        closeAudio();
                    }}
                    title="Close player"
                >
                    <i className="fas fa-times"></i>
                </button>
            </div>
        </div>
    );
};

export default MiniAudioPlayer; 